import { addYears, format, isAfter, isBefore, isValid, subYears } from 'date-fns';

import { convertDDMMYYYtoYYYYMMDD, forceLocalDate } from '../../../utils/dates';
import { usePredictManagementContext } from '../../../predict-management/context/predict-management-context';

const HARD_MIN_FORECAST_START_DATE = forceLocalDate('2021-01-01');
const MAX_FUTURE_YEARS = 3;
const MIN_PAST_YEARS = 2;

export interface ForecastStartDateRange {
  maxDate?: string;
  minDate?: string;
}

export const isForecastStartDateOutOfRange = (
  startDate: string,
  { maxDate, minDate }: ForecastStartDateRange
) => {
  const formattedStartDate = convertDDMMYYYtoYYYYMMDD(startDate.trim());

  if (!formattedStartDate || !maxDate || !minDate) {
    return false;
  }

  const startDateValue = forceLocalDate(formattedStartDate);
  const maxDateValue = forceLocalDate(maxDate);
  const minDateValue = forceLocalDate(minDate);

  return isBefore(startDateValue, minDateValue) || isAfter(startDateValue, maxDateValue);
};

/**
 * Returns the inclusive bounds for forecast Start date pickers.
 * The minimum date is two years before the organisation was created, but never before
 * 1 January 2021. The maximum date is three years after today. Returns no bounds when
 * the organisation creation date is unavailable.
 *
 * @param organisationCreated - Creation date supplied by form hosts outside the management context.
 */
export const useForecastStartDateRange = (organisationCreated?: string): ForecastStartDateRange => {
  const { organisation } = usePredictManagementContext();
  const created = organisationCreated ?? organisation?.created;

  if (!created) {
    return {};
  }

  const organisationCreatedDate = forceLocalDate(created);

  if (!isValid(organisationCreatedDate)) {
    return {};
  }

  const organisationMinDate = subYears(organisationCreatedDate, MIN_PAST_YEARS);
  const minDate = isBefore(organisationMinDate, HARD_MIN_FORECAST_START_DATE)
    ? HARD_MIN_FORECAST_START_DATE
    : organisationMinDate;

  return {
    maxDate: format(addYears(new Date(), MAX_FUTURE_YEARS), 'yyyy-MM-dd'),
    minDate: format(minDate, 'yyyy-MM-dd')
  };
};
